import React, { useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowRight, ChevronRight, RotateCcw, ShieldCheck } from 'lucide-react';
import { loadLaboratoryWorkflowVersion, loadLaboratoryWorkflows } from './api';
import { FieldGlyph, SpecimenGlyph, StageGlyph } from './LaboratoryGlyphs';
import { LabEmpty, LabResource } from './LaboratoryParts';
import { departmentName, departmentShort, fieldLabel, itemLabel, sampleTypeName, stageName, unitSymbol, workflowName } from './LaboratoryText';
import { LabDrawer } from './LaboratoryVisuals';
import { DeviceArt } from './XomicsLabDevices';
import { Head, Pill } from './parts';
import { useLiveResource } from './useLive';
import { tx } from './i18n';
import './laboratory-workflows.css';

const text = (key, values) => tx(`ui.lab.flow.${key}`, values);
const SHOWN_DEVICES = 3;
const published = workflow => workflow.versions.find(version => version.status === 'published') || workflow.versions[0];

// The choices of a field, under the method they belong to where they have one.
function choiceGroups(field, fields) {
  const items = field.list.items.filter(item => item.isActive);
  const parent = field.parentFieldId === null ? null : fields.find(candidate => candidate.id === field.parentFieldId);
  if (!parent) return [{ id: 'all', label: null, items }];
  return [
    ...parent.list.items.map(owner => ({ id: owner.id, label: itemLabel(owner), items: items.filter(item => item.parentItemId === owner.id) })),
    { id: 'any', label: null, items: items.filter(item => item.parentItemId === null) },
  ].filter(group => group.items.length > 0);
}

function Field({ field, fields }) {
  const unit = unitSymbol(field.unit);
  return <li className="xo-flow-field">
    <span className="xo-flow-field-icon" title={text(`type.${field.valueType}`)}><FieldGlyph code={field.valueType} /></span>
    <div>
      <p><strong>{fieldLabel(field.code)}</strong>{unit !== '' && <span className="xo-flow-unit">{unit}</span>}
        {field.isRequired && <span className="xo-flow-required">{text('required')}</span>}</p>
      {field.list !== null && choiceGroups(field, fields).map(group => <div className="xo-flow-options" key={group.id}>
        {group.label !== null && <small>{group.label}</small>}
        <div className="xo-flow-chips">{group.items.map(item => <span key={item.code}
          className={item.outcome ? `is-${item.outcome}` : ''}>{itemLabel(item)}</span>)}</div>
      </div>)}
    </div>
  </li>;
}

function StageDetails({ stage, onClose }) {
  return <LabDrawer title={stageName(stage.code)} eyebrow={`${text('stage_no', { position: stage.position })} · ${text(`kind.${stage.kind}`)}`} onClose={onClose}>
    {stage.approvalRequirement === 'required' && <p className="xo-flow-approval"><ShieldCheck aria-hidden="true" />{text('approval_hint')}</p>}
    <section className="xo-flow-detail">
      <h3>{text('recorded')}</h3>
      <ul className="xo-flow-field-list">{stage.fields.map(field => <Field key={field.id} field={field} fields={stage.fields} />)}</ul>
    </section>
    <section className="xo-flow-detail">
      <h3>{text('devices')}</h3>
      <p className="xo-lab-muted">{text(`device_rule.${stage.deviceRule}`)}</p>
      {stage.devices.length > 0 && <div className="xo-lab-role-machines">{stage.devices.map(device => (
        <Link className="xo-lab-role-machine xo-flow-device" key={device.id} to={`/xomics/laboratory/devices?device=${device.id}`}>
          <span className="xo-flow-device-art"><DeviceArt device={device} /></span>
          <strong>{device.name}<small>{device.inventoryNumber}</small></strong>
          {device.status !== 'in_service' && <Pill tone="bad">{tx(`ui.lab.device.status.${device.status}`)}</Pill>}
          <ChevronRight aria-hidden="true" />
        </Link>
      ))}</div>}
    </section>
    {stage.outcomeActions.length > 0 && <section className="xo-flow-detail">
      <h3>{text('if_fail')}</h3>
      <div className="xo-flow-chips is-fail">{stage.outcomeActions.map(action => <span key={action.nextAction.code}>
        <RotateCcw aria-hidden="true" />{tx(`ui.lab.next.${action.nextAction.code}`)}</span>)}</div>
    </section>}
  </LabDrawer>;
}

function StageCard({ stage, last, onOpen }) {
  const name = stageName(stage.code);
  const shown = stage.devices.slice(0, SHOWN_DEVICES);
  return <li className="xo-lab-flow-stage">
    <button type="button" className="xo-lab-stage-button" onClick={onOpen} aria-label={`${stage.position}. ${name} · ${tx('ui.lab.visual.details')}`}>
      <span className="xo-lab-step-index">{String(stage.position).padStart(2, '0')}</span>
      <span className="xo-lab-step-symbol"><StageGlyph code={stage.kind} /></span>
      <strong>{name}</strong>
      <small className="xo-flow-stage-count">{text(stage.fields.length === 1 ? 'records_one' : 'records', { count: stage.fields.length })}</small>
      {stage.approvalRequirement === 'required' && <span className="xo-lab-approval-mark" title={text('approval_required')}>
        <ShieldCheck aria-label={text('approval_required')} /></span>}
    </button>
    <div className="xo-lab-stage-equipment" role="group" aria-label={`${name} · ${tx('ui.lab.equipment')}`}>
      <span className="xo-lab-equipment-label">{tx('ui.lab.equipment')}</span>
      {shown.map(device => <Link key={device.id} to={`/xomics/laboratory/devices?device=${device.id}`} title={device.inventoryNumber}
        className={device.status === 'in_service' ? '' : 'is-off'}><DeviceArt device={device} /><span>{device.name}</span></Link>)}
      {stage.devices.length > shown.length && <span className="xo-lab-stage-neutral">{text('more_devices', { count: stage.devices.length - shown.length })}</span>}
      {stage.devices.length === 0 && <span className="xo-lab-stage-neutral">{text('no_device')}</span>}
    </div>
    {stage.outcomeActions.length > 0 && <p className="xo-flow-stage-fail"><RotateCcw aria-hidden="true" />
      <span>{stage.outcomeActions.map(action => tx(`ui.lab.next.${action.nextAction.code}`)).join(' · ')}</span></p>}
    {!last && <span className="xo-lab-stage-arrow" aria-hidden="true"><ArrowRight /></span>}
  </li>;
}

function WorkflowBoard({ versionId, stageCode, onStage }) {
  const load = useCallback(() => loadLaboratoryWorkflowVersion(versionId), [versionId]);
  const resource = useLiveResource(['laboratory'], load);
  return <LabResource resource={resource}>{({ workflow }) => {
    const stage = workflow.stages.find(candidate => candidate.code === stageCode);
    return <article className="xo-lab-board xo-flow-board" aria-label={workflow.code}>
      <header className="xo-lab-board-heading">
        <div><span className="xo-lab-family-caption">{departmentName(workflow.department.code)}</span>
          <h2>{workflow.code}</h2>{workflowName(workflow.code).toLowerCase() !== workflow.code.toLowerCase() && <p>{workflowName(workflow.code)}</p>}</div>
        <div className="xo-lab-board-meta"><Pill tone={workflow.status === 'published' ? 'ok' : 'flat'}>{text(`status.${workflow.status}`)}</Pill>
          <span className="xo-flow-version">{text('version', { version: workflow.versionNo })}</span></div>
      </header>
      <section className="xo-flow-samples" aria-label={text('accepted_samples')}>
        <div className="xo-lab-section-label"><h3>{text('accepted_samples')}</h3>
          <Link className="xo-mob-link" to={`/xomics/laboratory/analyses?analysis=${workflow.analysis.code}`}>{text('requirements_link')}<ChevronRight aria-hidden="true" /></Link></div>
        <ul>{workflow.sampleTypes.map(type => <li key={type.code}><span className="xo-lab-glyph"><SpecimenGlyph code={type.code} /></span>{sampleTypeName(type.code)}</li>)}</ul>
      </section>
      <section className="xo-lab-flow-section" aria-label={tx('ui.lab.stages')}>
        <div className="xo-lab-section-label"><h3>{tx('ui.lab.stages')}</h3><span>{tx('ui.lab.visual.open_stage')}</span></div>
        <ol className="xo-lab-flow" style={{ '--lab-stage-count': workflow.stages.length }}>{workflow.stages.map((item, index) => (
          <StageCard key={item.id} stage={item} last={index === workflow.stages.length - 1} onOpen={() => onStage(item.code)} />
        ))}</ol>
        <div className="xo-lab-flow-legend xo-flow-legend">
          <span><ShieldCheck aria-hidden="true" />{text('approval_required')}</span>
          <span className="is-fail"><RotateCcw aria-hidden="true" />{text('legend_fail')}</span>
        </div>
      </section>
      {stage && <StageDetails stage={stage} onClose={() => onStage(null)} />}
    </article>;
  }}</LabResource>;
}

export default function XomicsLabWorkflows() {
  const [params, setParams] = useSearchParams();
  const resource = useLiveResource(['laboratory'], loadLaboratoryWorkflows);
  return <div className="xo-platform-page xo-lab-page">
    <Head title={text('title')} subtitle={text('intro')} />
    <LabResource resource={resource}>{data => {
      const code = params.get('workflow') || (data.workflows[0] && data.workflows[0].code);
      const selected = data.workflows.find(workflow => workflow.code === code);
      const openStage = stage => setParams(stage === null ? { workflow: code } : { workflow: code, stage }, { replace: stage === null });
      return <>
        <nav className="xo-flow-tiles" aria-label={text('title')}>{data.workflows.map(workflow => {
          const current = published(workflow);
          return <button type="button" key={workflow.code} className="xo-flow-tile" aria-current={workflow.code === code ? 'page' : undefined}
            onClick={() => setParams({ workflow: workflow.code })}>
            <span className="xo-flow-tile-code">{workflow.code}</span>
            <strong>{workflowName(workflow.code)}</strong>
            <span className="xo-flow-tile-steps" aria-hidden="true">{Array.from({ length: current.stageCount }, (_, index) => <i key={index} />)}</span>
            <small title={departmentShort(workflow.departmentCode)}>{workflow.departmentCode} · {text('stage_count', { count: current.stageCount })}</small>
          </button>;
        })}</nav>
        {selected ? <WorkflowBoard key={published(selected).id} versionId={published(selected).id} stageCode={params.get('stage')} onStage={openStage} />
          : <LabEmpty />}
      </>;
    }}</LabResource>
  </div>;
}
